import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import LoadingCard from '@qpr/components/Card/LoadingCard';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import ActivityIcon from '../assets/rolebrowser/ActivityIcon';
import ApplicationIcon from '../assets/rolebrowser/ApplicationIcon';
import DocumentIcon from '../assets/rolebrowser/DocumentIcon';
import RoleIcon from '../assets/rolebrowser/RoleIcon';
import AppBarContext from '../components/BusinessPortalAppBarContext';
import HierarchyCard from '../modules/Components/Card/HierarchyCard';
import InfoCard from '../modules/Components/Card/InfoCard';
import SearchCard from '../modules/Components/Card/SearchCard';
import createElementsListViewModel from '../modules/Components/ElementList/views/elementsList-view-model';
import dfs from '../modules/Components/HierarchyTree/dfs';
import { isValidUrl } from '../utils/url-utils';

const PREFIX = 'RoleBrowser';

const classes = {
  card: `${PREFIX}-card`,
  pageHeader: `${PREFIX}-pageHeader`,
  flexContainer: `${PREFIX}-flexContainer`,
  takeRestOfTheSpace: `${PREFIX}-takeRestOfTheSpace`,
  hierarchy: `${PREFIX}-hierarchy`,
  info: `${PREFIX}-info`,
  search: `${PREFIX}-search`
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.card}`]: {
    height: '100%',
    width: '100%',
    padding: theme.spacing(2),
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    color: alpha(theme.palette.text.primary, 0.62)
  },

  [`& .${classes.pageHeader}`]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },

  [`& .${classes.flexContainer}`]: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },

  [`& .${classes.takeRestOfTheSpace}`]: {
    display: 'flex',
    flex: 1,
    marginTop: theme.spacing(1),
    overflow: 'hidden'
  },

  [`& .${classes.hierarchy}`]: {
    height: '480px'
  },

  [`& .${classes.info}`]: {
    height: '260px'
  },

  [`& .${classes.search}`]: {
    maxHeight: '700px'
  }
}));

interface Data {
  name: string;
  id: string;
  symbol?: string;
  description?: string;
  parentid?: Array<string>;
  children?: Array<Data>;
  link?: string;
  informationItems?: Data[];
}

const findById =
  (id: string) =>
  (curry: any[], item: any): any => {
    if (curry.length < 1) {
      return item.id === id ? [item] : item.children?.reduce(findById(id), []) || curry;
    }
    return curry;
  };

const useRelatedRoles = (meaId: string | undefined) => {
  const query = `@me = [${meaId}];
@roles = @me.onsubprocesses.subobjects.filter(include="OrganizationItem");
@ = @roles;
@ -= @me;`;

  return useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(
    `related-roles-${meaId}`,
    async () => {
      const response = await axios.post('meaquery', {
        query
      });

      return response.data;
    },
    {
      enabled: !!meaId,
      staleTime: 60 * 1000
    }
  );
};

const useActivities = (processes: Data[] | undefined, role: string) => {
  const cacheKey = role + processes?.map(process => process.id).join('-') || '-undefined';
  return useQuery(
    `activities${cacheKey}`,
    async () => {
      const requests = processes?.map(process =>
        axios.post('meaquery', {
          query: `[${process.id}].subobjects.filter(include="Activity", criteria="Find(\\"${role}\\", firstorganizationitem.name)")`
        })
      );

      if (requests?.length && processes?.length) {
        return (await Promise.all(requests)).map((activity, index) => {
          return {
            ...processes[index],
            children: activity.data?.data
          };
        });
      }
      return [];
    },
    {
      enabled: !!processes,
      staleTime: 60 * 1000
    }
  );
};

const useProcesses = (meaId: string | undefined) =>
  useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(
    `processes-${meaId}`,
    async () => {
      const response = await axios.post('meaquery', {
        query: `[${meaId}].onsubprocesses`
      });

      return response.data;
    },
    {
      enabled: !!meaId,
      staleTime: 60 * 1000
    }
  );

const useApplications = (query: string | undefined) =>
  useQuery(
    `applications-${query}`,
    async () => {
      const vmquery = `${query}.Filter(include="Activity").related(relation="relationends.usedbyfrom", criteria="typename=\\"Application Service\\"")`;
      const vm = createElementsListViewModel({ query: vmquery, sortBy: 'name' });
      const data = await vm.getData();
      return data;
    },
    {
      enabled: !!query,
      staleTime: 60 * 1000
    }
  );

const useDocuments = (query: string | undefined) =>
  useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(
    `documents-${query}`,
    async () => {
      const response = await axios.post('meaquery', {
        query: `${query}.informationitems`,
        attributes: `name,typename,infoitem.linklocation(as=link), functionasattribute(functionasattribute="informationitems", as="informationItemIds")`,
        sortBy: 'name'
      });

      return response.data;
    },
    {
      enabled: !!query,
      staleTime: 60 * 1000
    }
  );

const useRoles = (meaId: string, relation: string) =>
  useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(`roles-${meaId}`, async () => {
    const parts = meaId.split('.');
    const modelId = `${parts[0]}.${parts[1]}`;

    const response = await axios.post('hierarchy', {
      containerId: modelId,
      relation,
      elementtypes: ['Organization Item'],
      criteria: 'symbol<>"ET_ORGANIZATION"',
      recursive: true
    });
    return response.data;
  });

const getData = (data: Data[] | undefined, searchTerm: string): Data[] =>
  data?.length && searchTerm.length ? dfs(data, searchTerm) : data || [];

interface SearchTermState {
  roles: string;
  activities: string;
  documents: string;
  applications: string;
  relatedRoles: string;
}

interface RoleBrowserPathParams {
  meaId: string;
}

interface RoleBrowserSearchParams {
  relation: string;
}

const RoleBrowser = () => {
  const { setError } = useErrorContext();
  const { params } = useRouteMatch<RoleBrowserPathParams>();
  const { search } = useLocation<RoleBrowserSearchParams>();
  const searchParams = new URLSearchParams(search);

  const history = useHistory();

  const [selectedProcess, setSelectedProcess] = useState<Data | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<Data | null>(null);
  const [selectedRole, setSelectedRole] = useState<Data | null>(null);
  const [rootElementQuery, setRootElementQuery] = useState<string | undefined>(undefined);
  const [urlProcessed, setUrlProcessed] = useState<boolean>(false);

  const appBarContext = useContext(AppBarContext);

  useEffect(() => {
    appBarContext.setAppBar(true);
    appBarContext.setPeriodSelector(false);
  }, [appBarContext]);

  const pushHistorySearch = (
    roleid: string | null | undefined,
    processid: string | null | undefined,
    activityid: string | null | undefined,
    thesearch: string = search
  ) => {
    if (
      thesearch.startsWith('role=') ||
      thesearch.startsWith('process=') ||
      thesearch.startsWith('activity=')
    )
      thesearch = '';
    else {
      if (thesearch.includes('&role='))
        thesearch = thesearch.substring(0, thesearch.indexOf('&role='));
      if (thesearch.includes('&process='))
        thesearch = thesearch.substring(0, thesearch.indexOf('&process='));
      if (thesearch.includes('&activity='))
        thesearch = thesearch.substring(0, thesearch.indexOf('&activity='));
    }
    const roleeq = roleid
      ? `&role=${roleid}` /* : selectedRole?.id ? `&role=${selectedRole?.id}` */
      : '';
    const processeq = processid
      ? `&process=${processid}`
      : //      selectedProcess?.id
        //      ? `&process=${selectedProcess?.id}` :
        '';
    const activityeq = activityid
      ? `&activity=${activityid}`
      : //      selectedActivity?.id
        //      ? `&activity=${selectedActivity?.id}` :
        '';
    history.push({
      pathname: '',
      search: `${thesearch || 'x=x'}${roleeq}${processeq}${activityeq}`
    });
  };

  const [searchTerms, setSearchTerms] = useState<SearchTermState>({
    roles: '',
    activities: '',
    documents: '',
    applications: '',
    relatedRoles: ''
  });

  const handeRoleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerms({ ...searchTerms, roles: event.target.value });
  };

  const handleActivitySearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerms({ ...searchTerms, activities: event.target.value });
  };

  const handleDocumentSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerms({ ...searchTerms, documents: event.target.value });
  };

  const handleApplicationsSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerms({ ...searchTerms, applications: event.target.value });
  };

  const handleRelatedRolesSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerms({ ...searchTerms, relatedRoles: event.target.value });
  };

  const roles = useRoles(params.meaId, searchParams.get('relation') || '');
  const relatedRoles = useRelatedRoles(selectedRole?.id);
  const processes = useProcesses(selectedRole?.id);
  const activities = useActivities(processes?.data?.data, selectedRole?.name || '');
  const applications = useApplications(rootElementQuery);
  const documents = useDocuments(rootElementQuery);

  useEffect(() => {
    async function applyRoleProcessActivity(
      relation: string | null,
      roleid: string | null,
      processid: string | null,
      activityid: string | null
    ) {
      let roledata = null;
      let processdata = null;
      let activitydata = null;

      if (!relation) roleid = null;

      const attributes =
        'name, id, symbol, description, parentid, children, link, informationItems';
      try {
        if (roleid) {
          const parts = roleid.split('.');
          const modelId = `${parts[0]}.${parts[1]}`;

          if (
            //  prove that roleid belongs into relation hierarchy
            roleid ===
            (
              await axios.post('hierarchy', {
                containerId: modelId,
                relation,
                elementtypes: ['Organization Item'],
                recursive: true
              })
            ).data.data[0].roles.find((x: string) => x === roleid)
          )
            roledata = (
              await axios.post('meaquery', {
                query: `[${roleid}]`,
                attributes
              })
            ).data.data[0];
        }
        if (!roledata) {
          processid = null;
          if (roleid) setError({ message: 'No role found', details: 'No found role by passed id' });
        }

        if (roleid && processid) {
          processdata = (
            await axios.post('meaquery', {
              query: `[${roleid}].onsubprocesses`,
              attributes
            })
          ).data.data.find((x: Data) => x.id === processid);
        }

        if (!processdata) activityid = null;
        if (processid && activityid)
          activitydata = (
            await axios.post('meaquery', {
              query: `[${processid}].subobjects`,
              criteria: 'typename="Activity"',
              attributes
            })
          ).data.data.find((x: Data) => x.id === activityid);
      } catch (e) {
        console.error(e);
      }
      setSelectedRole(roledata);
      setSelectedProcess(processdata);
      setSelectedActivity(activitydata);

      if (activitydata) setRootElementQuery(`[${activityid}]`);
      else if (processdata) setRootElementQuery(`[${processid}].subobjects`);
      else if (roledata) setRootElementQuery(`[${roleid}].onsubprocesses.subobjects`);
      else setRootElementQuery(undefined);

      setUrlProcessed(true);
    }
    applyRoleProcessActivity(
      searchParams.get('relation'),
      searchParams.get('role'),
      searchParams.get('process'),
      searchParams.get('activity')
    );
    pushHistorySearch(
      searchParams.get('role'),
      searchParams.get('process'),
      searchParams.get('activity')
    );

    history.listen(() => {
      if (history.action === 'POP') {
        setUrlProcessed(false);
        const urlparams = new URLSearchParams(history.location.search);
        applyRoleProcessActivity(
          urlparams.get('relation'),
          urlparams.get('role'),
          urlparams.get('process'),
          urlparams.get('activity')
        );
      }
    });
  }, []);

  if (roles.isLoading || !urlProcessed)
    return (
      <div
        data-testid="rolebrowser-page-loading"
        style={{ textAlign: 'center', marginTop: '32px' }}
      >
        <CircularProgress />
      </div>
    );
  const handleRoleChange = (role: Data) => {
    setSelectedActivity(null);
    setSelectedProcess(null);
    setSelectedRole(role);
    setSearchTerms({
      roles: searchTerms.roles,
      activities: '',
      documents: '',
      applications: '',
      relatedRoles: ''
    });

    setRootElementQuery(`[${role.id}].onsubprocesses.subobjects`);
    pushHistorySearch(role.id, null, null);
  };

  const handleActivityChange = (activity: Data) => {
    setSelectedActivity(activity);
    setRootElementQuery(`[${activity.id}]`);

    let processid = null;
    if (activity?.parentid?.length) {
      const process = processes?.data?.data?.reduce(findById(activity.parentid[0]), []);
      processid = activity.parentid[0];
      setSelectedProcess(process ? process[0] : null);
    } else setSelectedProcess(null);
    pushHistorySearch(selectedRole?.id, processid, activity.id);
  };

  const handleProcessChange = (process: Data) => {
    setSelectedActivity(null);
    setSelectedProcess(process);
    setRootElementQuery(`[${process.id}].subobjects`);
    pushHistorySearch(selectedRole?.id, process.id, null);
  };

  if (!roles.data || !roles.data.data) {
    return null;
  }
  if (roles.error && roles.error.response) setError(roles.error.response.data);

  const rolesData = roles.data.data.reduce(findById(params.meaId), []);

  return (
    <StyledContainer data-testid="rolebrowser-page" maxWidth={false}>
      <Typography
        className={`${classes.pageHeader} customHeader`}
        variant="h5"
        data-testid="rolebrowser-page-title"
      >
        {`Role browser - ${roles.data.data[0].name}`}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12} md={12} lg={4} xl={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={6} lg={12} className={classes.hierarchy}>
              <HierarchyCard
                title="Roles"
                className={classes.card}
                data-testid="rolebrowser-roles-card"
                onSearchChange={handeRoleSearchChange}
                onParentClick={handleRoleChange}
                onChildClick={handleRoleChange}
                data={getData(rolesData, searchTerms.roles)}
                searchTerm={searchTerms.roles}
                ChildIcon={RoleIcon}
                selectedIds={[selectedRole?.id]}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={12} className={classes.hierarchy}>
              {processes.isLoading || activities.isLoading ? (
                <LoadingCard
                  data-testid="rolebrowser-relatedroles-card-loading"
                  className={classes.card}
                />
              ) : (
                <HierarchyCard
                  title="Processes and Activities"
                  className={classes.card}
                  data-testid="rolebrowser-process-activity-card"
                  onSearchChange={handleActivitySearchChange}
                  onParentClick={handleProcessChange}
                  onChildClick={handleActivityChange}
                  data={getData(activities?.data, searchTerms.activities)}
                  searchTerm={searchTerms.activities}
                  ChildIcon={ActivityIcon}
                  selectedIds={[selectedProcess?.id, selectedActivity?.id]}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={8} xl={9}>
          <div className={classes.flexContainer}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={4} className={classes.info}>
                <InfoCard
                  className={classes.card}
                  data-testid="rolebrowser-selected-role-card"
                  title="Selected Role"
                  value={selectedRole?.name || 'No selected role'}
                  subtitle="Description"
                  subvalue={selectedRole?.description || 'No description'}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} className={classes.info}>
                <InfoCard
                  className={classes.card}
                  data-testid="rolebrowser-selected-process-card"
                  title="Selected Process"
                  value={selectedProcess?.name || 'No selected process'}
                  subtitle="Description"
                  subvalue={selectedProcess?.description || 'No description'}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4} className={classes.info}>
                <InfoCard
                  className={classes.card}
                  data-testid="rolebrowser-selected-activity-card"
                  title="Selected Activity"
                  value={selectedActivity?.name || 'No selected activity'}
                  subtitle="Description"
                  subvalue={selectedActivity?.description || 'No description'}
                />
              </Grid>
            </Grid>
            <div className={classes.takeRestOfTheSpace}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={4} className={classes.search}>
                  {documents.isLoading ? (
                    <LoadingCard
                      data-testid="rolebrowser-documents-card-loading"
                      className={classes.card}
                    />
                  ) : (
                    <SearchCard
                      className={classes.card}
                      data-testid="rolebrowser-documents-card"
                      title="Documents"
                      data={getData(documents.data?.data, searchTerms.documents).filter(
                        item => !item.link || isValidUrl(item.link)
                      )}
                      onClick={console.log}
                      onClose={() => setSearchTerms({ ...searchTerms, documents: '' })}
                      onSearch={handleDocumentSearchChange}
                      icon={DocumentIcon}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={4} className={classes.search}>
                  {applications.isLoading ? (
                    <LoadingCard
                      data-testid="rolebrowser-applications-card-loading"
                      className={classes.card}
                    />
                  ) : (
                    <SearchCard
                      className={classes.card}
                      title="Applications"
                      data-testid="rolebrowser-applications-card"
                      data={getData(applications.data?.data, searchTerms.applications).filter(
                        item =>
                          item?.informationItems?.length || !item.link || isValidUrl(item.link)
                      )}
                      onClick={console.log}
                      onClose={() => setSearchTerms({ ...searchTerms, applications: '' })}
                      onSearch={handleApplicationsSearchChange}
                      icon={ApplicationIcon}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={4} className={classes.search}>
                  {relatedRoles.isLoading ? (
                    <LoadingCard
                      data-testid="rolebrowser-relatedroles-card-loading"
                      className={classes.card}
                    />
                  ) : (
                    <SearchCard
                      className={classes.card}
                      title="Related Roles"
                      data={getData(relatedRoles.data?.data, searchTerms.relatedRoles)}
                      onClick={console.log}
                      onClose={() => setSearchTerms({ ...searchTerms, relatedRoles: '' })}
                      onSearch={handleRelatedRolesSearchChange}
                      icon={RoleIcon}
                    />
                  )}
                </Grid>
              </Grid>
            </div>
          </div>
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default RoleBrowser;
